import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { PurchaseProjectionModel } from 'src/app/models/preliminaries/purchase-projection.model';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { PurchaseProjectionService } from 'src/app/services/preliminaries/purchase-projection.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-purchase-projection',
  templateUrl: './register-purchase-projection.component.html',
  styleUrls: ['./register-purchase-projection.component.scss'],
  standalone: false
})
export class RegisterPurchaseProjectionComponent implements OnInit {

  public readonly branchService = inject(BranchService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly fb = inject(FormBuilder);
  private readonly purchaseProjectionService = inject(PurchaseProjectionService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  branchId: number = 0;
  formPurchaseProjection: FormGroup = new FormGroup({});
  listArticles: CustomListDropdown[] = [];
  listBranch: CustomListDropdown[] = [];
  permissionUpdatable: boolean = false;
  purchaseProjectionContract: PurchaseProjectionModel = new PurchaseProjectionModel();
  purchaseProjectionId: number = 0;
  subscription: Subscription;

  async ngOnInit() {
    this.validateForm(new PurchaseProjectionModel());
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    await this.getAllLists();
    this.subscription = input$.subscribe(async (inputs: any) => {
      this.purchaseProjectionId = inputs.purchaseProjectionId ?? 0;
      this.permissionUpdatable = inputs.permissionUpdatable ?? false;
      await this.handleInitialState();
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeBranch(branchId: number) {
    this.branchId = branchId;
    this.resetContract();
  }

  createNewPurchaseProjection() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear proyecciones de compra. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.resetContract();
  }

  async createPurchaseProjection(model: PurchaseProjectionModel) {
    try {
      const response = await firstValueFrom(this.purchaseProjectionService.createPurchaseProjection(model));
      this.purchaseProjectionId = response.purchaseProjectionId;
      this.purchaseProjectionContract = response;
      this.validateForm(this.purchaseProjectionContract);
      this.toastHandlerService.showToast(
        'success',
        'La proyección de compra fue creada exitosamente.'
      );
      this.onReloadEmit();
    } catch (error) {
      console.error('Error al crear la proyección de compra.', error);
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'articles'>) {
    try {
      const lists = {
        branches: async () => {
          const response = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
          this.listBranch = response;
        },
        articles: async () => {
          const response = await firstValueFrom(this.purchaseProjectionService.getArticlesForSelect());
          this.listArticles = response;
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.articles()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getFirstBranch() {
    try {
      const response = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la sucursal del usuario.', error);
    }
  }

  async getPurchaseProjectionById() {
    try {
      const id = this.purchaseProjectionId;
      const response = await firstValueFrom(this.purchaseProjectionService.getPurchaseProjectionById(id));
      this.branchId = response.branchId;
      this.purchaseProjectionContract = response;
      this.validateForm(this.purchaseProjectionContract);
    } catch (error) {
      console.error('Error al obtener la proyección de compra por ID.', error);
    }
  }

  async getPurchaseProjectionByKeys(articleId: number) {
    if (!articleId) {
      this.resetContract();
      return;
    }
    try {
      const response = await firstValueFrom(this.purchaseProjectionService.getPurchaseProjectionByKeys(
        this.branchId, articleId
      ));
      this.purchaseProjectionId = response.purchaseProjectionId;
      this.purchaseProjectionContract = response;
      this.validateForm(this.purchaseProjectionContract);
    } catch (error) {
      console.error('Error al obtener la proyección de compra por claves.', error);
    }
  }

  async handleInitialState() {
    if (this.purchaseProjectionId) {
      await this.getPurchaseProjectionById();
    } else {
      await this.getFirstBranch();
      this.createNewPurchaseProjection();
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  async resetContract() {
    this.purchaseProjectionId = 0;
    this.purchaseProjectionContract = new PurchaseProjectionModel();
    this.purchaseProjectionContract.branchId = this.branchId;
    this.validateForm(this.purchaseProjectionContract);
  }

  async send() {
    this.validateFormControls();
    if (!this.formPurchaseProjection.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formPurchaseProjection.getRawValue() as PurchaseProjectionModel;
    if (this.purchaseProjectionId == 0) {
      await this.createPurchaseProjection(model);
    } else {
      await this.updatePurchaseProjection(model);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debes seleccionar una Sucursal válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.',
        minlength: 'Debe seleccionar un Artículo válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'La cantidad mínima permitida es 1.'
      },
      datePurchaseProjection: {
        required: 'El campo Fecha proyección compra es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formPurchaseProjection.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updatePurchaseProjection(model: PurchaseProjectionModel) {
    try {
      this.purchaseProjectionContract.quantity = model.quantity;
      this.purchaseProjectionContract.datePurchaseProjection = model.datePurchaseProjection;
      const response = await firstValueFrom(this.purchaseProjectionService.updatePurchaseProjection(this.purchaseProjectionContract));
      this.purchaseProjectionContract = response;
      this.validateForm(this.purchaseProjectionContract);
      this.toastHandlerService.showToast(
        'success',
        'La proyección de compra fue modificada exitosamente.'
      );
      this.onReloadEmit();
    } catch (error) {
      console.error('Error al actualizar la proyección de compra.', error);
    }
  }

  validateForm(purchaseProjection: PurchaseProjectionModel) {
    this.formPurchaseProjection = this.fb.group({
      branchId: [purchaseProjection.branchId, [Validators.required, Validators.min(1)]],
      articleId: [purchaseProjection.articleId, [Validators.required, Validators.min(1), Validators.minLength(4)]],
      quantity: [purchaseProjection.quantity, [Validators.required, Validators.min(1)]],
      datePurchaseProjection: [purchaseProjection.datePurchaseProjection ?
        this.dateConverterService.convertUtcToLocalDate(purchaseProjection.datePurchaseProjection)
        : null, [Validators.required]],
    });
  }

  validateFormControls() {
    Object.keys(this.formPurchaseProjection.controls).forEach((key) => {
      const control = this.formPurchaseProjection.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
